import { Box } from '@timelyapp/react';
import ContentResizer from 'components/ContentResizer';
import CurrencyFormat from 'components/CurrencyFormat';
import DurationFormat from 'components/DurationFormat';
import { Scrollbars } from 'components/scrollbars';
import TimelyTooltip from 'components/Tooltip';
import WidgetTooltip from 'components/ui-blocks/widget/WidgetTooltip';
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/defaults';
import { COLUMN_DONUT_PERCENTAGE, LOW_DONUT_THRESHOLD } from 'constants/filters';
import {
  WIDGET_BILLABLE_VS_NON_BILLABLE_DONUT,
  WIDGET_BILLED_VS_UNBILLED_DONUT,
  WIDGET_LABELS_DONUT,
} from 'constants/widgets';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { TooltipProps } from 'recharts';
import { Pie, PieChart, Tooltip } from 'recharts';
import { extractSingleCurrency } from 'redux/modules/invoice.utils';
import type { DonutDataItem, WidgetOptions } from 'redux/modules/superreports.types';
import { WidgetDataType } from 'redux/modules/superreports.types';
import { cssVariables } from 'styles';
import type { Currency } from 'types/account';
import { TagFilterMode } from 'types/tag';
import { InfoIcon } from '../../../Icons';
import { NO_DATA_TAG_FILTER_MESSAGE, REPORT_DURATION_FORMAT } from '../constants';
import NoData from '../NoData';
import styles from './DonutWidget.module.scss';
import DonutWidgetListItems from './DonutWidgetListItems';
import DonutWidgetLoading from './DonutWidgetLoading';
import {
  ACTIVE_DONUT_SECTOR_GROWTH,
  ActiveDonutSector,
  renderDonutWidgetPieCells,
} from './DonutWidgetPieCells';
import { DONUT_THICKNESS, getGroupLabel, setActiveTooltip } from './helpers';

const LEGIBLE_PERCENT_THRESHOLD = 4;

interface Props {
  widgetType: string;
  data?: Array<DonutDataItem>;
  options?: WidgetOptions;
  columns: Array<string>;
  isLoading: boolean;
  billedState: boolean | null;
  tagFilterMode: TagFilterMode;
  thresholdFactor?: number;
  total?: number;
  totalTooltipText?: string;
}

const donutInnerRadius = cssVariables.widgetBaseHeight / 2 - DONUT_THICKNESS * 2;
const donutOuterRadius =
  cssVariables.widgetBaseHeight / 2 - DONUT_THICKNESS - ACTIVE_DONUT_SECTOR_GROWTH;

const labelMaxWidth = (donutInnerRadius - ACTIVE_DONUT_SECTOR_GROWTH) * 2;

const DonutWidgetBody: React.FC<Props> = ({
  widgetType,
  data = EMPTY_ARRAY,
  options = EMPTY_OBJECT as WidgetOptions,
  isLoading,
  billedState,
  columns = EMPTY_ARRAY,
  thresholdFactor,
  tagFilterMode,
  total,
  totalTooltipText,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | undefined>();
  const pieChartRef = useRef<InstanceType<typeof PieChart> | null>(null);
  const singleCurrency = extractSingleCurrency(data);
  const showPercentage = columns.includes(COLUMN_DONUT_PERCENTAGE);

  const renderValue = useMemo(() => {
    if (options.dataType === 'money' && widgetType !== WIDGET_BILLABLE_VS_NON_BILLABLE_DONUT) {
      return (value: number, className: string, currency?: Currency) => (
        <CurrencyFormat className={className} value={value} currency={currency} />
      );
    } else {
      return (value: number, className: string) => (
        <DurationFormat
          className={className}
          value={value}
          valueType="hours"
          format={REPORT_DURATION_FORMAT}
        />
      );
    }
  }, [options.dataType, widgetType]);

  const renderTooltip = useCallback(
    ({ active, payload }: TooltipProps<number, string>) => {
      const item: (typeof data)[number] | undefined = payload?.[0]?.payload;
      if (!active || !item) return null;

      return (
        <WidgetTooltip
          title={item.name}
          subTitle={item.subHeading}
          value={item.value}
          valueType={options.dataType}
          percentOfTotal={showPercentage ? item.percentOfTotal : null}
          currency={singleCurrency}
          billingSummary={billedState === null ? item.billingSummary : undefined}
        />
      );
    },
    [options.dataType, showPercentage, singleCurrency, billedState]
  );

  const onListItemMouseOver = useCallback((index: number) => {
    setActiveIndex(index);
    setActiveTooltip(pieChartRef.current, index);
  }, []);

  const onListItemMouseLeave = useCallback(() => {
    setActiveIndex(undefined);
    setActiveTooltip(pieChartRef.current);
  }, []);

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    percent,
  }: {
    cx: number;
    cy: number;
    midAngle: number;
    percent: number;
  }) => {
    // labels will overlap if the slice is smaller than ~4%,
    // so always hide labels for slices smaller than this
    const percentageIsBelowThreshold =
      thresholdFactor === LOW_DONUT_THRESHOLD && percent * 100 < LEGIBLE_PERCENT_THRESHOLD;

    if (!showPercentage || percentageIsBelowThreshold) {
      return null;
    }
    const RADIAN = Math.PI / 180;
    const x = cx + 120 * Math.cos(-midAngle * RADIAN);
    const y = cy + 120 * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="black" textAnchor="middle" dominantBaseline="central" fontSize={13}>
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  if (isLoading) {
    return <DonutWidgetLoading />;
  }

  if (widgetType === WIDGET_BILLED_VS_UNBILLED_DONUT && billedState !== null) {
    return <NoData message={`This widget only works with "Total" selected.`} />;
  }

  if (options.dataType === WidgetDataType.Money && !singleCurrency && data.length) {
    return <NoData message="This widget only works with a single currency." />;
  }

  if (widgetType === WIDGET_LABELS_DONUT && tagFilterMode === TagFilterMode.AND) {
    return <NoData message={NO_DATA_TAG_FILTER_MESSAGE} />;
  }

  const entityCount = data.length + (data[data.length - 1]?.mergedItems?.length ?? 1) - 1;
  const valueSum = total != null ? total : data.reduce((sum, { value }) => sum + value, 0);
  if (!entityCount || !valueSum) {
    return <NoData />;
  }

  const totalContent = renderValue(valueSum, styles.sum, singleCurrency);

  return (
    <div className={styles.donut}>
      <div className={styles.chart}>
        <PieChart
          width={cssVariables.widgetBaseHeight + DONUT_THICKNESS}
          height={cssVariables.widgetBaseHeight}
          onMouseLeave={() => setActiveIndex(undefined)}
          ref={pieChartRef}
        >
          <Pie
            activeIndex={activeIndex}
            activeShape={ActiveDonutSector}
            data={data}
            dataKey="value"
            innerRadius={donutInnerRadius}
            outerRadius={donutOuterRadius}
            paddingAngle={1}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            startAngle={450}
            endAngle={90}
            animationBegin={0}
            animationDuration={1000}
            // Having animations active on a component where parent re-renders alot breaks labels
            // https://github.com/recharts/recharts/issues/829 https://github.com/recharts/react-smooth/issues/44
            isAnimationActive={false}
            labelLine={false}
            label={renderCustomizedLabel}
          >
            {renderDonutWidgetPieCells(data)}
          </Pie>
          <Tooltip
            content={renderTooltip}
            wrapperStyle={{
              zIndex: 1000, // Needs to be on top of chart Summary
            }}
          />
        </PieChart>
        <div className={styles.summary}>
          <ContentResizer maxWidth={labelMaxWidth}>
            {totalTooltipText ? (
              <TimelyTooltip title={totalTooltipText} position="right">
                <Box.Row alignItems="center" gap="medium" style={{ pointerEvents: 'all' }}>
                  {totalContent}
                  <InfoIcon />
                </Box.Row>
              </TimelyTooltip>
            ) : (
              totalContent
            )}
          </ContentResizer>
          <span className={styles.count}>
            {[WIDGET_BILLED_VS_UNBILLED_DONUT, WIDGET_BILLABLE_VS_NON_BILLABLE_DONUT].includes(
              widgetType
            )
              ? 'total'
              : getGroupLabel(entityCount, widgetType)}
          </span>
        </div>
      </div>
      <div className={styles.legend}>
        <Scrollbars>
          <DonutWidgetListItems
            activeIndex={activeIndex}
            currency={singleCurrency}
            data={data}
            handleMouseOver={onListItemMouseOver}
            handleMouseLeave={onListItemMouseLeave}
            renderValue={renderValue}
          />
        </Scrollbars>
      </div>
    </div>
  );
};

export default React.memo(DonutWidgetBody);
